import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../Css/ManagementPage.css';
import { CircularProgress, Typography, TextField, Button, Dialog, Menu, MenuItem, DialogContentText, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Tooltip } from 'react-tooltip';
import { MdDownloadForOffline, MdOutlineUploadFile } from 'react-icons/md';
import Logger from './Logger';
import Form from 'react-bootstrap/Form';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';
import { format } from 'date-fns';
import PaginationControls from "../Component/Pagination";
import Navbar from '../Component/Navbar';
import { FaListUl } from "react-icons/fa";
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const Ledgers = () => {
    const userEmail = sessionStorage.getItem('userEmail');
    const [activeTab, setActiveTab] = useState('ledgerList');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedLedgers, setSelectedLedgers] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [invalidExcelLink, setInvalidExcelLink] = useState('');
    const [uploadError, setUploadError] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [validCount, setValidCount] = useState(0);
    const [invalidCount, setInvalidCount] = useState(0);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editLedgerDetails, setEditLedgerDetails] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 100;
    const [currentLedgers, setCurrentLedgers] = useState([]);

    const logger = useRef(new Logger(getLoggerBaseURL())).current;

    const handleOpenErrorModal = () => {
        setIsErrorModalOpen(true);
    };
    const handleModalClose = () => {
        setIsErrorModalOpen(false); // Close the modal
        fetchLedgers(); // Reload the page
    };

    const fetchLedgers = useCallback(async () => {
        setLoading(true); // Start loading

        try {
            const response = await fetch(`/api/ledger/fetchLedgers/${userEmail}`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setLedgers(data || []);


        } catch (error) {
            logger.log(`Error fetching data: ${error.message}`, 'ERROR');
            setLedgers([]);
        } finally {
            setLoading(false); // End loading
        }
    }, [userEmail, logger]);

    useEffect(() => {
        fetchLedgers();
    }, [fetchLedgers]);

    const handleSelectAll = (isChecked) => {
        if (isChecked) {
            setSelectedLedgers(filteredLedgers.map(ledgers => ledgers.ledgerId));
        } else {
            setSelectedLedgers([]);
        }
    };

    const handleEditInputChange = (field, value) => {
        setEditLedgerDetails((prevDetails) => ({
            ...prevDetails,
            [field]: value,
        }));
    };

    const handleThreeDotsClick = (event, orderId) => {
        if (anchorEl === event.currentTarget) {
            // If the same anchorEl is clicked again, close the dropdown
            setAnchorEl(null);
        } else {
            // Otherwise, set the anchorEl to the clicked element
            setAnchorEl(event.currentTarget);
            setSelectedOrderId(orderId); // Set the selected order ID
        }
    };

    const handleEdit = (ledgerId) => {
        const ledge = ledgers.find((d) => d._id === ledgerId);
        if (ledge) {
            setEditLedgerDetails(ledge);
            setIsEditModalOpen(true);

        } else {
            logger.log("Driver not found", 'ERROR');
        }
    };

    const handleSaveEdit = async () => {
        if (!editLedgerDetails) return;

        // Checking for required fields
        if (!editLedgerDetails?.address || !editLedgerDetails?.del_date || !editLedgerDetails?.phone || !editLedgerDetails.pick_date) {
            alert('Field can not be empty');
            return;
        }

        // Check phone number length
        if (editLedgerDetails.phone.length !== 10) {
            alert('Enter a valid phone number');
            return;
        }

        try {
            const adminEmail = sessionStorage.getItem('adminEmail');
            const response = await fetch(`/api/ledger/update/${editLedgerDetails._id}/${adminEmail}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(editLedgerDetails),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to update ledgers: ${errorText}`);
            }

            const updatedLedger = await response.json();
            setLedgers((prevLedgers) =>
                prevLedgers.map((ledgers) =>
                    ledgers._id === updatedLedger._id ? updatedLedger : ledgers
                )
            );

            setIsEditModalOpen(false);
        } catch (error) {
            logger.log(`Error updating ledgers: ${error.message}`, 'ERROR');
        }
    };

    const handleDeleteLedger = async () => {
        if (!selectedOrderId) return;

        try {

            const adminEmail = sessionStorage.getItem('adminEmail');
            const response = await fetch(`/api/ledger/delete/${selectedOrderId}/${adminEmail}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                throw new Error('Failed to delete ledgers');
            }

            setLedgers((prevLedgers) => prevLedgers.filter((ledgers) => ledgers._id !== selectedOrderId));

            setIsDeleteDialogOpen(false);
            setAnchorEl(null);
        } catch (error) {
            logger.log(`Error deleting ledgers: ${error.message}`, 'ERROR');
        }
    };

    const downloadErrorMessages = () => {
        const element = document.createElement('a');
        const file = new Blob([uploadError], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = 'error_messages.txt';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        setIsErrorModalOpen(false); // Close the modal after downloading
    };

    const handleSelectLedger = (orderId, isChecked) => {
        if (isChecked) {
            setSelectedLedgers([...selectedLedgers, orderId]);
        } else {
            setSelectedLedgers(selectedLedgers.filter(id => id !== orderId));
        }
    };

    const downloadDemoExcel = () => {
        const link = document.createElement('a');
        link.href = "/assets/LedgerSample.xlsx";
        link.download = "LedgerSample.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleOpenDeleteDialog = () => {
        setIsDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleFileUpload = async (event) => {

        const file = event.target.files[0];
        const formData = new FormData();
        formData.append('file', file);

        setLoading(true); // Start loading

        try {
            const response = await fetch(`/api/ledger/api/upload/${userEmail}`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {
                alert('Data Uploaded successfully');
                fetchLedgers();
            } else {
                // Format the errors for display
                let errorMessage = 'Some rows were not saved due to validation errors:\n';
                if (Array.isArray(data.errors)) {
                    errorMessage += data.errors
                        .map(errorObj => `Errors: ${errorObj.errors.join(', ')}`)
                        .join('\n\n');
                } else {
                    errorMessage = 'Incorrect Format';
                }
                setUploadError(errorMessage);
                setValidCount(data.vCount);
                setInvalidCount(data.iCount);
                setInvalidExcelLink(data.downloadUrl);
                handleOpenErrorModal(); // Open the error modal
            }

            logger.log(`Uploaded file data: ${data}`, 'INFO');
        } catch (error) {
            setUploadError(error.message); // Set the upload error
            handleOpenErrorModal(); // Open the error modal
        } finally {
            setLoading(false); // End loading
        }
    };

    const filteredLedgers = ledgers?.filter((ledger) =>
        ledger.ledger?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    useEffect(() => {
        if (filteredLedgers) {
            const indexOfLastItem = currentPage * itemsPerPage;
            const indexOfFirstItem = indexOfLastItem - itemsPerPage;
            setCurrentLedgers(filteredLedgers.slice(indexOfFirstItem, indexOfLastItem));
        }
    }, [filteredLedgers, currentPage, itemsPerPage]);

    return (
        <div className="mgmt-page">
            <Navbar />

            <div className="mgmt-page-header">
                <div>
                    <h2>Ledgers</h2>
                </div>
                <div className="mgmt-actions">
                    <div className="mgmt-search">
                        <Form style={{ width: '100%', height: '100%', margin: 0 }}>
                            <Form.Control
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder='Search...'
                            />
                        </Form>
                    </div>

                    <button className="mgmt-icon-btn" onClick={downloadDemoExcel} data-tooltip-id="download-ledgers-tooltip">
                        <MdDownloadForOffline size={30} />
                    </button>
                    <Tooltip id="download-ledgers-tooltip" place="bottom" content="Download Ledgers Sample Excel" />

                    <div className="mgmt-icon-btn">
                        <input accept=".xlsx,.xls" style={{ display: 'none' }} id="upload-ledgers-file" type="file" onChange={handleFileUpload} autocomplete="off" />
                        <label htmlFor="upload-ledgers-file" className="mgmt-upload-label">
                            <div data-tooltip-id="upload-ledgers-tooltip">
                                <MdOutlineUploadFile size={30} />
                            </div>
                        </label>
                    </div>
                    <Tooltip id="upload-ledgers-tooltip" place="bottom" content="Upload Ledgers Excel" />
                </div>
            </div>

            <div className="mgmt-card">
                {loading ? (
                    <div className="mgmt-loading">
                        <CircularProgress />
                        <Typography variant="h6" style={{ marginTop: '20px' }}>Fetching data...</Typography>
                    </div>
                ) : (
                    <>
                        <table className="cTable mgmt-table">
                            <thead>
                                <tr>
                                    <th>
                                        <input type="checkbox" onChange={(e) => handleSelectAll(e.target.checked)} checked={currentLedgers.length > 0 && selectedLedgers?.length === currentLedgers.length} autocomplete="off" />
                                    </th>
                                    <th className="thC" style={{ width: "1%" }}><strong>Ledger No.</strong></th>
                                    <th className="thC" style={{ width: "15%" }}><strong>Ledger Name</strong></th>
                                    <th className="thC" style={{ width: "21%" }}><strong>Ledger Address</strong></th>
                                    <th className="thC"><strong>Ledger Phone</strong></th>
                                    <th className="thC"><strong>Req. Pickup Time</strong></th>
                                    <th className="thC"><strong>Req. Pickup Date</strong></th>
                                    <th className="thC"><strong>Req. Del Time</strong></th>
                                    <th className="thC"><strong>Req. Del Date</strong></th>
                                    <th className="thC"><strong>Actions</strong></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLedgers.length === 0 ? (
                                    <tr>
                                        <td className="noOrders" colSpan="10">
                                            <div className="center-content">
                                                <div className="icon-wrapper">
                                                    <FaListUl className="icon" />
                                                </div>
                                                <span className="noOrdersMsg">No Ledger found</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    currentLedgers.map(ledgers => (
                                        <tr key={ledgers.ledgerId}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLedgers?.includes(ledgers.ledgerId)}
                                                    onChange={(e) => handleSelectLedger(ledgers.ledgerId, e.target.checked)}
                                                    autocomplete="off"
                                                />
                                            </td>
                                            <td className="tdC">{ledgers.ledgerId || 'N/A'}</td>
                                            <td className="tdC">{ledgers.ledger || 'N/A'}</td>
                                            <td className="tdC">{ledgers.address || 'N/A'}</td>
                                            <td className="tdC">{ledgers.phone || 'N/A'}</td>
                                            <td className="tdC">{ledgers.pick_time || 'N/A'}</td>
                                            <td className="tdC">{ledgers.pick_date || 'N/A'}</td>
                                            <td className="tdC">{ledgers.del_time || 'N/A'}</td>
                                            <td className="tdC">{ledgers.del_date || 'N/A'}</td>
                                            <td className="tdCu">
                                                <button className="Dbutton" onClick={(e) => handleThreeDotsClick(e, ledgers._id)}>

                                                    ...
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
            <div className="mgmt-pagination">
                <PaginationControls totalItems={ledgers.length} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
            </div>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                sx={{

                    "& .MuiPaper-root": {
                        boxShadow: '1px 2px 2px 0px rgba(0, 0, 0, 0.1);',  // Light shadow
                        backgroundColor: '#fff',  // Background color
                        borderRadius: '8px',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        "&:hover": {
                            transform: 'scale(1.02)',
                            boxShadow: '0px 6px 18px rgba(0, 0, 0, 0.2)',
                        },
                    },
                    "& .MuiMenuItem-root": {
                        transition: 'background-color 0.3s ease, color 0.3s ease',  // Smooth transition for color changes
                        "&:hover": {
                            backgroundColor: '#f0f0f0',  // Light gray background on hover for menu items
                            color: ' #79c6e9',  // Change text color on hover (optional)
                        },
                    },
                }}
            >
                <MenuItem onClick={() => handleEdit(selectedOrderId)}><FaEdit style={{ marginRight: '8px' }} />  Edit</MenuItem>
                <MenuItem onClick={handleOpenDeleteDialog}> <FaTrashAlt style={{ marginRight: '8px' }} />  Delete</MenuItem>
            </Menu>

            <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog} >
                <DialogTitle>Delete Ledger</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this ledgers?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} style={{ backgroundColor: ' #79c6e9', color: 'black', border: '1px solid #ccc', borderRadius: '4px', padding: '6px 12px', }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteLedger} style={{ backgroundColor: 'white', color: 'black', border: '1px solid #ccc', borderRadius: '4px', padding: '6px 12px', }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ style: { height: '80vh', width: '500px', overflowY: 'auto', }, }} >
                <DialogTitle>Edit Ledger</DialogTitle>
                <DialogContent>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <TextField label="Name" value={editLedgerDetails?.ledger || ''} onChange={(e) => handleEditInputChange('ledger', e.target.value)} fullWidth style={{ flex: 1, marginRight: '10px', marginTop: '10px' }}
                            InputProps={{ readOnly: true, style: { color: 'black' } }} />
                        <TextField
                            label="+91 Phone No"
                            value={editLedgerDetails?.phone || ''}
                            onChange={(e) => {
                                const phoneInput = e.target.value;

                                // Allow only numeric values and ensure length is no more than 10 digits
                                if (phoneInput.length <= 10 && /^[0-9]*$/.test(phoneInput)) {
                                    handleEditInputChange('phone', phoneInput);
                                }
                            }}
                            fullWidth
                            style={{ flex: 1, marginTop: '10px' }}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <TextField
                            label="Pickup Time"
                            type="time"
                            value={
                                editLedgerDetails?.pick_time
                                    ? (() => {
                                        const [time, modifier] = editLedgerDetails?.pick_time.split(" ");
                                        let [hours, minutes] = time.split(":");

                                        if (hours === "12") {
                                            hours = "00";
                                        }

                                        if (modifier === "PM") {
                                            hours = parseInt(hours, 10) + 12;
                                        }

                                        return `${hours.toString().padStart(2, '0')}:${minutes}`;
                                    })()
                                    : ''
                            }
                            onChange={(e) => handleEditInputChange('pick_time', e.target.value)}
                            fullWidth
                            style={{ flex: 1, marginRight: '10px', marginTop: '10px' }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            label="Pickup Date"
                            type='date'
                            value={editLedgerDetails?.pick_date ? format(new Date(editLedgerDetails.pick_date), 'yyyy-MM-dd') : ''}
                            onChange={(e) => handleEditInputChange('pick_date', e.target.value)}
                            fullWidth
                            style={{ flex: 1, marginTop: '10px' }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <TextField label="Delivery Time" type="time" value={editLedgerDetails?.del_time || ''} onChange={(e) => handleEditInputChange('del_time', e.target.value)} fullWidth style={{ flex: 1, marginRight: '10px', marginTop: '10px' }}
                            InputLabelProps={{
                                shrink: true,
                            }} />
                        <TextField
                            label="Delivery Date"
                            type='date'
                            value={editLedgerDetails?.del_date ? format(new Date(editLedgerDetails.del_date), 'yyyy-MM-dd') : ''}
                            onChange={(e) => handleEditInputChange('del_date', e.target.value)}
                            fullWidth

                            style={{ flex: 1, marginTop: '10px' }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </div>

                    <TextField label="Address" value={editLedgerDetails?.address || ''} onChange={(e) => handleEditInputChange('address', e.target.value)} fullWidth multiline rows={3} style={{ marginTop: '20px' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsEditModalOpen(false)} style={{ backgroundColor: 'white', color: 'black', border: '1px solid #ccc', borderRadius: '4px', padding: '6px 12px', }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveEdit} style={{ backgroundColor: ' #79c6e9', color: 'black', border: '1px solid #ccc', borderRadius: '4px', padding: '6px 12px', }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isErrorModalOpen} onClose={handleModalClose}>
                <DialogTitle>Upload Summary</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" style={{ color: 'green', marginBottom: '20px' }}>
                        Correct ledgers uploaded successfully: {validCount}
                    </Typography>
                    <Typography variant="body1" style={{ marginBottom: '10px' }}>
                        Invalid ledgers not saved due to validation errors : {invalidCount}
                    </Typography>
                    {Array.isArray(uploadError) ? (
                        uploadError.map((errorObj, index) => (
                            <div key={index} style={{ marginBottom: '10px' }}>
                                <Typography variant="body1">
                                    <strong>Order No:</strong> {errorObj.orderNo}
                                </Typography>
                                <Typography variant="body2" style={{ color: 'red', marginLeft: '20px' }}>
                                    <strong>Errors:</strong> {errorObj.errors.join(', ')}
                                </Typography>
                            </div>
                        ))
                    ) : (
                        <Typography variant="body1" style={{ color: 'red' }}>
                            {uploadError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleModalClose}>Cancel</Button>
                    <Button onClick={downloadErrorMessages} variant="contained" color="primary">
                        Download Errors
                    </Button>
                    {invalidExcelLink && (
                        <Button
                            onClick={async () => {
                                try {
                                    const response = await fetch(`${invalidExcelLink}`);

                                    if (!response.ok) {
                                        throw new Error('Failed to download file');
                                    }

                                    const blob = await response.blob();
                                    const url = window.URL.createObjectURL(blob);

                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = 'invalid_rows.xlsx';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);
                                } catch (error) {
                                    logger.log(`Download error: ${error.message}`, 'ERROR');
                                }
                            }}
                            variant="contained"
                            color="secondary"
                        >
                            Download Invalid Excel
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Ledgers;